import { createUseStatementScreenProps } from '@/src/experiences/card-statements/Statement/createUseStatementScreenProps'
import { Api } from '@/src/store'
import { useCardAccount } from '@/src/experiences/card-accounts/hooks/useCardAccount'
import { ID } from '@acre-rewards/lib'
import {
  type StatementComponent,
  type StatementScreenProvider,
  type StatementScreenParams,
  UnreadyStatementsProvider,
  ErrorStatementsProvider,
} from '@/src/experiences/card-statements/Statement/withStatementScreen'

const usePlatformStatementScreenProps = createUseStatementScreenProps(
  (_, cardAccountId: ID.CardAccount, statementId: ID.Statement) => {
    const query = Api.endpoints[
      'GET /card/:cardAccountId/statements/:statementId/pdf-link'
    ].useQuery({ params: { cardAccountId, statementId } })

    return query.isLoading || query.isUninitialized
      ? { loading: true }
      : query.isSuccess
        ? { pdfLink: query.data }
        : { error: query.error }
  },
)

export const PlatformStatement: StatementComponent = ({ ToWrap, statementScreenParams }) => {
  const query = useCardAccount()
  return query.isLoading || query.isUninitialized ? (
    <UnreadyStatementsProvider ToWrap={ToWrap} statementScreenParams={statementScreenParams} />
  ) : !query.isSuccess || !query.data[0]?.id ? (
    <ErrorStatementsProvider ToWrap={ToWrap} statementScreenParams={statementScreenParams} />
  ) : (
    <PlatformStatementWithCardAccount
      ToWrap={ToWrap}
      statementScreenParams={statementScreenParams}
      cardAccountId={query.data[0].id}
    />
  )
}

function PlatformStatementWithCardAccount({
  ToWrap,
  statementScreenParams,
  cardAccountId,
}: {
  ToWrap: StatementScreenProvider
  statementScreenParams: StatementScreenParams
  cardAccountId: ID.CardAccount
}) {
  const result = ID.schema.Statement.safeParse(statementScreenParams.id)
  return result.success ? (
    <PlatformStatementWithStatementId
      ToWrap={ToWrap}
      statementScreenParams={statementScreenParams}
      cardAccountId={cardAccountId}
      statementId={result.data}
    />
  ) : (
    <ErrorStatementsProvider ToWrap={ToWrap} statementScreenParams={statementScreenParams} />
  )
}

function PlatformStatementWithStatementId({
  ToWrap,
  statementScreenParams,
  cardAccountId,
  statementId,
}: {
  ToWrap: StatementScreenProvider
  statementScreenParams: StatementScreenParams
  cardAccountId: ID.CardAccount
  statementId: ID.Statement
}) {
  return (
    <ToWrap
      statementScreenProps={usePlatformStatementScreenProps(
        statementScreenParams,
        cardAccountId,
        statementId,
      )}
    />
  )
}
